import { useEffect, useState } from "react";
import { fetchDay } from "@/lib/api";
import { pad } from "@/lib/format";
import { dueEntries, useSrsSnapshot } from "@/hooks/useSrs";
import type { Word } from "@/types/lex";

interface DueWord extends Word {
  day: number;
}

const PREVIEW = 5;

/**
 * "Due today" summary for the archive page: how many cards the schedule has
 * ready, and a peek at the first few. Only the days behind the preview rows
 * are fetched, so a backlog spread over 30 days still costs a handful of
 * (cached, same-origin) reads.
 */
export function DuePanel({ onStart }: { onStart: () => void }) {
  const srs = useSrsSnapshot();
  const [preview, setPreview] = useState<readonly DueWord[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const due = dueEntries(Date.now());
    setTotal(due.length);
    if (due.length === 0) {
      setPreview([]);
      return;
    }

    const head = due.slice(0, PREVIEW);
    const days = Array.from(new Set(head.map((d) => d.day)));
    Promise.all(days.map((d) => fetchDay(d)))
      .then((results) => {
        if (cancelled) return;
        const byDay = new Map(results.map((r) => [r.day, r]));
        const words: DueWord[] = [];
        for (const entry of head) {
          const word = byDay.get(entry.day)?.words.find((w) => w.index === entry.index);
          if (word) words.push({ ...word, day: entry.day });
        }
        setPreview(words);
      })
      .catch(() => {
        // The count is the useful part; a failed preview just renders empty.
        if (!cancelled) setPreview([]);
      });

    return () => {
      cancelled = true;
    };
    // Recompute whenever the schedule is written to — finishing a review on
    // another screen should be reflected here.
  }, [srs]);

  if (total === 0) return null;

  return (
    <div className="due-panel">
      <div className="row-s" style={{ marginBottom: "var(--s-3)" }}>
        <span className="t-eyebrow">Due today</span>
        <span className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {pad(total)} {total === 1 ? "card" : "cards"}
        </span>
      </div>
      <div className="due-list">
        {preview.map((w) => (
          <div className="due-row" key={`${w.day}:${w.index}`}>
            <span className="dot dot--indigo" />
            <span className="due-row__word">{w.word}</span>
            <span className="due-row__day">D{pad(w.day)}</span>
            <span className="due-row__meaning">{w.meaning_vi}</span>
          </div>
        ))}
        {total > preview.length && (
          <div className="due-row due-row--more t-mono" style={{ fontSize: 11 }}>
            + {total - preview.length} more
          </div>
        )}
      </div>
      <button
        className="btn btn-solid"
        onClick={onStart}
        style={{ width: "100%", justifyContent: "center", marginTop: "var(--s-4)" }}
        type="button"
      >
        Start review →
      </button>
    </div>
  );
}
